// authFetch: thin fetch wrapper that always attaches the Supabase Bearer token.
// Use this in place of raw fetch() whenever calling /api/* endpoints that
// require a tenant/auth context. Returns the raw Response so callers keep
// their existing error-handling style.
//
// Phase B-1 observability: also captures the response's x-request-id into
// the global clientLogger ring buffer and surfaces non-2xx responses on
// the global error-toast bus so the operator sees a CRSC-XXXX code +
// Copy button without every caller having to plumb its own toast.

import { getToken, getCachedToken, forceRefreshToken, clearTokenCache } from './authToken';

let _redirectInFlight = false;

// Clear module-level token cache. Must be called on signOut so the next
// user's tab on the same browser doesn't briefly carry the previous user's
// Bearer token into the first authenticated request. Routes to the unified
// cache in authToken.ts; kept under this name for backwards compatibility.
export function clearAuthFetchCache() {
  clearTokenCache();
}

function buildHeaders(init: RequestInit, token: string | null): Headers {
  const headers = new Headers(init.headers || {});
  if (token) headers.set('Authorization', `Bearer ${token}`);
  const imp = typeof window !== 'undefined' ? localStorage.getItem('crewsync_impersonate_tenant') : null;
  if (imp && !headers.has('X-Impersonate-Tenant')) {
    headers.set('X-Impersonate-Tenant', imp);
  }
  return headers;
}

async function handleSessionExpired() {
  if (_redirectInFlight) return;
  _redirectInFlight = true;
  try {
    const { supabase } = await import('./supabase');
    await supabase.auth.signOut().catch(() => {});
  } catch {}
  // Hard reload to clear all in-memory state and land on /login.
  if (typeof window !== 'undefined') {
    window.location.href = '/login';
  }
}

// Decode a JWT's exp claim without verifying. Used only to answer
// "is the token we hold *locally* already past its expiry?" before we
// conclude that a 401 means the session is really dead. A local JWT that
// still has minutes left on its clock but got 401'd means the server had
// a transient issue (Supabase auth timeout, rate limit, network blip),
// not that the session is truly invalid.
function jwtIsStillLocallyValid(token: string | null): boolean {
  if (!token) return false;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return false;
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    const exp = typeof payload?.exp === 'number' ? payload.exp : 0;
    // 10s skew to avoid borderline-expired tokens riding into retry.
    return exp * 1000 > Date.now() + 10_000;
  } catch {
    return false;
  }
}

// Routes whose non-2xx responses are expected during normal browsing
// (e.g. bell poller on a freshly-loaded tab). Suppress the global toast
// for these so we don't carpet the screen with CRSC codes during boot.
const TOAST_SUPPRESS = [
  '/api/notifications',
  '/api/connection/status',
  '/api/profile',
  '/api/bug-reports', // self-loop avoidance
  '/api/client-errors',
];

function urlIsSuppressed(input: RequestInfo | URL): boolean {
  let url = '';
  try {
    if (typeof input === 'string') url = input;
    else if (input instanceof URL) url = input.toString();
    else url = input.url;
  } catch { return false; }
  return TOAST_SUPPRESS.some((p) => url.includes(p));
}

export async function authFetch(input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> {
  const token = await getToken();
  const res = await fetch(input, { ...init, headers: buildHeaders(init, token) });

  // Phase B-1: capture x-request-id on every response (success or fail)
  // and surface non-2xx responses on the global toast bus. We do this
  // BEFORE the existing 401/503 retry logic so the toast fires even when
  // the retry path eventually succeeds — it didn't, on the original.
  try {
    const reqId = res.headers.get('x-request-id');
    if (reqId || !res.ok) {
      // Lazy import to avoid pulling client-logger into every SSR build
      // codepath. The dynamic import is resolved synchronously after the
      // first call so the cost is paid once.
      const mod = await import('./client-logger');
      if (reqId) mod.noteRequestId(reqId);
      if (!res.ok && res.status !== 401 && res.status !== 503 && !urlIsSuppressed(input)) {
        // Read envelope without consuming the body — caller still owns it.
        let envelope: { code?: string; error_id?: string; category?: string; message_for_operator?: string } = {};
        try {
          const body = await res.clone().json().catch(() => null);
          if (body && typeof body === 'object' && body.error && typeof body.error === 'object') {
            envelope = body.error;
          }
        } catch { /* swallow */ }
        mod.recordBackendError({
          ts: new Date().toISOString(),
          status: res.status,
          request_id: reqId || envelope.error_id || undefined,
          error_code: envelope.code,
          error_id: envelope.error_id,
          category: envelope.category,
          url: typeof input === 'string' ? input : (input instanceof URL ? input.toString() : input.url),
          message_for_operator: envelope.message_for_operator,
        });
        const toastMod = await import('./error-toast');
        toastMod.showErrorToast({
          code: envelope.code,
          error_id: envelope.error_id,
          category: envelope.category,
          message_for_operator: envelope.message_for_operator,
          request_id: reqId,
          url: typeof input === 'string' ? input : (input instanceof URL ? input.toString() : input.url),
          status: res.status,
        });
      }
    }
  } catch { /* observability must never break a real request */ }

  // The server now returns 503 auth_timeout when its own Supabase lookup
  // times out (upstream slowness, not a dead session). Let the caller retry
  // on its own schedule — do NOT sign the user out.
  if (res.status === 503) {
    try {
      const body = await res.clone().json().catch(() => null);
      if (body?.error === 'auth_timeout') return res;
    } catch {}
  }

  // On 401 invalid_token, refresh the Supabase session once and retry. If the
  // retry still 401s AND the locally-held JWT is already past its own expiry,
  // the session is truly dead — sign out and redirect.
  //
  // Critical guard: if our local JWT still has minutes of validity left,
  // a 401 from the server is *not* a dead session. It's more likely a
  // transient Supabase auth-backend slowness that tipped past the server's
  // 20s timeout. Signing the user out here was the direct cause of the
  // "idle tab kicks me to /login" bug — the notifications bell polls every
  // few seconds, so any transient server blip would evict the user.
  if (res.status === 401) {
    try {
      const clone = res.clone();
      const body = await clone.json().catch(() => null);
      if (body?.error === 'invalid_token') {
        const fresh = await forceRefreshToken();
        if (fresh && fresh !== token) {
          const retry = await fetch(input, { ...init, headers: buildHeaders(init, fresh) });
          if (retry.status !== 401) return retry;
        }
        // Still 401 after refresh. Only conclude "session dead" when the
        // locally-held token has itself expired. Otherwise keep the session
        // alive and let the caller see the 401.
        const current = getCachedToken();
        if (!jwtIsStillLocallyValid(current)) {
          handleSessionExpired();
        }
      }
    } catch { /* fall through with original response */ }
  }

  return res;
}
